<script setup lang="ts">
import { computed, nextTick, ref, toRef, useTemplateRef, watch } from "vue";

import { cardActions } from "@/action/cardActions";
import { cardSizeFrom } from "@/board/board-utils";
import StickyNote from "@/components/StickyNote/StickyNote.vue";
import ActionSource from "@/components/action/ActionSource.vue";
import LoadingIndicator from "@/components/ui/LoadingIndicator.vue";
import type { Board } from "@/model/board";
import type { BoardCard } from "@/model/card";
import type { BoardCoordinate } from "@/model/coordinates";
import { relativeCoord } from "@/model/coordinates";
import { useBoardStore } from "@/store/board";
import { boundsCoord } from "@/utils/dom/coordinates";
import { isKeyDown } from "@/utils/shortcut/shortcuts";

import BacklogAddNewButton from "./internal/BacklogAddNewButton.vue";
import BacklogMenu from "./internal/BacklogMenu.vue";
import { useFluidBoard } from "./internal/useFluidBoard";

// used for the new sticky note, since we have a progress bar on the bottom
const gapMultiplier = 0.057;

const props = defineProps<{ board: Board }>();

const rootRef = useTemplateRef("root");

const { active, isLoading, levelOfDetails, readonly, focusAndBlur, boardSize } = useFluidBoard(
  toRef(props, "board"),
  rootRef,
);

defineExpose(focusAndBlur);

const addStickyButtonRef = useTemplateRef<HTMLButtonElement>("addStickyButton");

const columns = ref(0);
const gridSize = ref(0);

const cards = computed(() =>
  Object.values(props.board.cards).sort((a, b) => {
    const prio = b.data.priority - a.data.priority;
    // if priorities are equal, assure there's still a defined order
    return prio !== 0 ? prio : a.data.id > b.data.id ? 1 : -1;
  }),
);

const visibleCards = computed(() => cards.value.filter(show));
const gap = computed(() => (gridSize.value * gapMultiplier) / 2);
const cardSize = computed(() => gridSize.value - gap.value * 2);

watch<[number, BoardCoordinate]>(
  () => [visibleCards.value.length + 1, boardSize.value],
  ([count, board]) => {
    // Skip when the board is inactive (this watcher will be triggered
    // as the board is activated)
    if (!active.value) return;

    if (count <= 10) {
      return setColumnsAndSize(5, board.x / 5);
    }
    const h = board.y * 0.8;
    const w = board.x;
    const ac = Math.ceil(Math.sqrt((w / h) * count));
    const bc = Math.ceil(Math.sqrt((h / w) * count));

    const bw = Math.floor(w / (h / bc));
    const bw2 = Math.floor(w / (h / (bc + 1)));
    const a1 = ac * Math.floor(h / (w / ac));
    const a2 = (ac + 1) * Math.floor(h / (w / (ac + 1)));
    const b1 = bw * bc;
    if (a1 >= count && (b1 < count || a1 <= b1)) {
      return setColumnsAndSize(ac, w / ac);
    }
    if (a2 >= count && (b1 < count || a2 <= b1)) {
      return setColumnsAndSize(ac + 1, w / (ac + 1));
    }
    if (b1 >= count) {
      return setColumnsAndSize(bw, h / bc);
    }
    return setColumnsAndSize(bw2, h / (bc + 1));
  },
);

function setColumnsAndSize(cols: number, s: number) {
  columns.value = cols;
  gridSize.value = s;
  void updateCardPos();
}

watch(() => visibleCards.value.length, updateCardPos, { immediate: true });

watch<[BoardCoordinate, number, Board]>(
  // We have to explicitly watch the currentBoard, so this gets triggered when this board activates
  () => [boardSize.value, cardSize.value, useBoardStore().currentBoard()],
  ([board, card, currentBoard]) => {
    if (currentBoard.id !== props.board.id) return; // Only recalculate if this is the current board

    if (board.x !== 0 && board.y !== 0) {
      currentBoard.cardSize = cardSizeFrom({ cardWidth: card, boardWidth: board.x });
    }
  },
);

async function updateCardPos() {
  await nextTick(); // wait for width and height to be non 0
  visibleCards.value.map((card, i) => {
    card.meta.pos = cardMiddlePos(i);
  });
}

function add() {
  const fromKeyboard = isKeyDown("Enter");

  if (!fromKeyboard) {
    void cardActions.add("backlog-board-plus-button", { pos: "auto" }, true);
    return;
  }

  // If the user pressed enter, open the context menu at the same position as the button
  // and return focus to the trigger when the menu is closed
  const onClose = () => setTimeout(() => addStickyButtonRef.value?.focus(), 0);
  const cardProps = { pos: boundsCoord(addStickyButtonRef.value!), onClose };
  void cardActions.add("backlog-board-plus-button", cardProps, true);
}

function cardMiddlePos(position: number) {
  const cardMiddle = cardSize.value / 2;
  return relativeCoord(
    (left(position) + cardMiddle) / boardSize.value.x,
    (top(position) + cardMiddle) / boardSize.value.y,
  );
}

function top(position: number) {
  const rowNumber = Math.floor(position / columns.value);
  const previousCardRowsHeight = rowNumber * gridSize.value;
  const topMargin = boardSize.value.y * 0.1;
  return topMargin + previousCardRowsHeight;
}

function left(position: number) {
  const colNumber = position % columns.value;
  const previousCardColsWidth = colNumber * gridSize.value;
  return previousCardColsWidth + gap.value;
}

function show(card: BoardCard) {
  return card.meta.mark !== "filter-out";
}
</script>

<template>
  <div :id="board.id" ref="root" class="board board-backlog">
    <LoadingIndicator v-if="active && isLoading" global />
    <div class="backdrop">
      <ActionSource value="board-button">
        <BacklogMenu />
      </ActionSource>
    </div>
    <StickyNote
      v-for="card in visibleCards"
      :key="card.data.id"
      :card="card.data"
      :card-meta="card.meta"
      :level-of-details
      no-drag
    />
    <BacklogAddNewButton
      v-if="!readonly"
      ref="addStickyButton"
      class="add"
      :style="{
        top: top(visibleCards.length) + 'px',
        left: left(visibleCards.length) + 'px',
        height: cardSize + 'px',
        width: cardSize + 'px',
      }"
      @click="add"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/a11y";

.backdrop {
  position: relative;
  align-items: baseline;
  display: flex;
  justify-content: right;
  font-size: inherit;
  padding-top: 1em;
  padding-right: 1em;

  h2 {
    flex: 1;
  }
}

.board {
  @include a11y.board;
}
</style>

<style lang="scss">
@use "@/styles/mixins/a11y";

.base-button.medium.add {
  @include a11y.sticky-note;

  position: absolute;

  // The plus icon
  svg {
    width: 20%;
    height: 20%;
  }
}
</style>
